import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ban, CheckSquare, Copy, Eye, MoreHorizontal, Pause, Pencil, Play, RotateCcw, Trash2 } from "lucide-react";
import { del, post } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConfirmModal } from "@/components/atelier/AtelierModals";
import { useAuth } from "@/lib/auth";
import { canWritePilotage } from "@/lib/permissions";

export const useOfActions = () => {
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["of"] }); qc.invalidateQueries({ queryKey: ["of-detail"] }); qc.invalidateQueries({ queryKey: ["kpi-planificateur"] }); qc.invalidateQueries({ queryKey: ["archives"] }); };
  return useMutation({
    mutationFn: ({ method, url, body }) => (method === "delete" ? del(url) : post(url, body)),
    onSuccess: (_, v) => { toast.success(v.ok); refresh(); },
    onError: (e) => toast.error(e.message),
  });
};

export const OfActionsMenu = ({ of, onEdit, showFiche = true }) => {
  const navigate = useNavigate();
  const act = useOfActions();
  const [confirm, setConfirm] = useState(null);
  const run = (url, ok, method) => act.mutate({ url, ok, method });
  const s = of.statut;
  const Item = ({ icon: Icon, children, onClick, testId, danger }) => (
    <DropdownMenuItem onClick={onClick} data-testid={testId} className={`text-xs gap-2 ${danger ? "text-red-400 focus:text-red-300" : ""}`}><Icon className="h-3.5 w-3.5" />{children}</DropdownMenuItem>
  );
  const confirms = {
    cloturer: { title: "Clôturer l'OF ?", text: `${of.numero} sera clôturé et son TRS final figé.`, label: "Clôturer", tone: "danger", go: () => run(`/of/${of.id}/cloturer`, "OF clôturé") },
    annuler: { title: "Annuler l'OF ?", text: `${of.numero} passera au statut Annulé.`, label: "Annuler l'OF", tone: "danger", go: () => run(`/of/${of.id}/annuler`, "OF annulé") },
    supprimer: { title: "Supprimer l'OF ?", text: `${of.numero} (jamais démarré) sera supprimé définitivement.`, label: "Supprimer", tone: "danger", go: () => run(`/of/${of.id}`, "OF supprimé", "delete") },
  };
  const c = confirm && confirms[confirm];
  const { user } = useAuth();
  if (!canWritePilotage(user.role)) return null;   // le superviseur consulte : aucune action d'écriture sur les OF
  return (<>
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button data-testid={`of-actions-${of.id}`} onClick={(e) => e.stopPropagation()} className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm min-w-[190px]" onClick={(e) => e.stopPropagation()}>
        {showFiche && <Item icon={Eye} testId={`of-action-fiche-${of.id}`} onClick={() => navigate(`/of/${of.id}`)}>Fiche OF</Item>}
        {s !== "cloture" && s !== "annule" && <Item icon={Pencil} testId={`of-action-edit-${of.id}`} onClick={() => onEdit(of)}>Modifier</Item>}
        <Item icon={Copy} testId={`of-action-dupliquer-${of.id}`} onClick={() => run(`/of/${of.id}/dupliquer`, "OF dupliqué (statut prévu)")}>Dupliquer</Item>
        <DropdownMenuSeparator />
        {s === "prevu" && <Item icon={Play} testId={`of-action-demarrer-${of.id}`} onClick={() => run(`/of/${of.id}/demarrer`, "OF démarré")}>Démarrer</Item>}
        {s === "en_production" && <Item icon={Pause} testId={`of-action-suspendre-${of.id}`} onClick={() => run(`/of/${of.id}/suspendre`, "OF suspendu (Pause OF)")}>Suspendre</Item>}
        {s === "pause" && <Item icon={RotateCcw} testId={`of-action-reprendre-${of.id}`} onClick={() => run(`/of/${of.id}/reprendre`, "OF repris")}>Reprendre</Item>}
        {(s === "en_production" || s === "pause") && <Item icon={CheckSquare} testId={`of-action-cloturer-${of.id}`} onClick={() => setConfirm("cloturer")}>Clôturer</Item>}
        {["prevu", "en_production", "pause"].includes(s) && <Item icon={Ban} danger testId={`of-action-annuler-${of.id}`} onClick={() => setConfirm("annuler")}>Annuler</Item>}
        {s === "prevu" && <Item icon={Trash2} danger testId={`of-action-supprimer-${of.id}`} onClick={() => setConfirm("supprimer")}>Supprimer</Item>}
      </DropdownMenuContent>
    </DropdownMenu>
    {c && <ConfirmModal open onClose={() => setConfirm(null)} title={c.title} text={c.text} confirmLabel={c.label} tone={c.tone} busy={act.isPending} onConfirm={() => { c.go(); setConfirm(null); }} />}
  </>);
};
